import {SagaIterator} from "redux-saga";
import {call, delay, put, select, take} from "typed-redux-saga";
import {
	getIsTeamHasBeenChanged,
	getPlayerById,
	getProfile,
	getTeam,
	getTeamPlayersIds,
	isLoggedIn,
} from "modules/selectors";
import {last, map} from "lodash";
import {
	addPlayerToTeam,
	continueFetchData,
	fetchSharingImageSuccess,
	fetchUserTeams,
	fetchUserTeamsFailed,
	fetchUserTeamsSuccess,
	openDontLoseTeamModal,
	removePlayerFromTeam,
	saveUserTeamClear,
	saveUserTeamSuccess,
	setLineup,
	setTeamChanged,
	sharingImageLoaded,
	toggleErrorModal,
	toggleSuccessModal,
} from "modules/actions";
import {PlayerPositionEnum} from "modules/enums";
import {ISagaAction, ISaveTeamPayload, ITeam, IWithLanguage, Lineup} from "modules/types";
import {Api, ApiError} from "modules/utils/api";
import {ConnextraType, createConnextraScriptTag} from "modules/utils/analytics";
import {TEAM_KEY} from "modules/constants";
import i18next from "i18next";

export const clearAllPlayersSaga = function* (): SagaIterator {
	// unused
	try {
		const team = yield* select(getTeam);
		const keys = Object.keys(team) as PlayerPositionEnum[];

		for (const key of keys) {
			yield* put(removePlayerFromTeam(key));
		}
	} catch (e) {
		console.log(e);
	}
};

export const fetchUserTeamsSaga = function* ({
	payload: isNeedSetLocalTeam,
}: ReturnType<typeof fetchUserTeams>): SagaIterator {
	try {
		const profile = yield* select(getProfile);
		const hasTerms = Boolean(profile?.terms);

		if (!hasTerms) {
			yield* put(fetchUserTeamsFailed());
			yield* take(continueFetchData);
		}

		const response = yield* call(Api.Team.getHistory);

		ApiError.CHECK(response);

		yield* put(fetchUserTeamsSuccess(response.success));

		if (!isNeedSetLocalTeam) {
			return;
		}

		// show un saved team
		yield* call(setUnSavedTeamSaga);

		// add fan vote player
		yield* call(setFanVotePlayerSaga);
	} catch (e) {
		yield* put(fetchUserTeamsFailed());
		const error = e as ApiError;
		yield* put(toggleErrorModal(error.message));
	}
};

const setUnSavedTeamSaga = function* () {
	const team = JSON.parse(localStorage.getItem(TEAM_KEY) || "false") as Lineup;

	if (team) {
		yield* put(setLineup(team));
	}

	localStorage.removeItem(TEAM_KEY);
};

const setFanVotePlayerSaga = function* () {
	const playerId = new URLSearchParams(window.location.search).get("id");

	if (!playerId) {
		return;
	}

	const selector = yield* select(getPlayerById);
	const player = selector(+playerId);

	if (player) {
		yield* put(addPlayerToTeam({playerId: player.id, position: player.position}));
	}
};

const trackAnalytics = function (team: ITeam) {
	const playerIds = map(team.lineup, (id) => ({player_id: id}));
	const season = Number(
		i18next.t("season", "", {
			ns: "gamezone",
		})
	);

	window.Trialfire.track("pick_submitted", {
		game: "allstar",
		season,
		players: playerIds,
		type: "regular_vote",
	});

	window.rudderanalytics.track("pick_submitted", {
		game: "allstar",
		season,
		players: playerIds,
		type: "regular_vote",
	});
};

export const saveUserTeamSaga = function* (): SagaIterator {
	try {
		const team = yield* select(getTeamPlayersIds);
		const isLogged = yield* select(isLoggedIn);

		if (!isLogged) {
			localStorage.setItem(TEAM_KEY, JSON.stringify(team));
			yield* put(openDontLoseTeamModal());
			return;
		}

		const data: ISaveTeamPayload = {lineup: team};
		const response = yield* call(Api.Team.save, data);

		ApiError.CHECK(response);

		const savedTeam = last(response.success.history);

		if (!savedTeam) {
			return;
		}

		yield* put(saveUserTeamSuccess(response.success));
		yield* put(toggleSuccessModal(savedTeam));
		trackAnalytics(savedTeam);
	} catch (e) {
		const error = e as ApiError;
		yield* put(toggleErrorModal(error.message));
	} finally {
		yield* delay(1000);
		yield* put(saveUserTeamClear());
	}
};

export const AddPlayerToTeamSideSaga = function* (): SagaIterator {
	try {
		const isTeamChanged = yield* select(getIsTeamHasBeenChanged);

		if (!isTeamChanged) {
			createConnextraScriptTag(ConnextraType.PICKS_START, null);
		}
		yield* put(setTeamChanged());
	} catch (e) {
		console.log(e);
	}
};

export const fetchTeamShareImageSaga = function* ({
	payload,
}: ISagaAction<IWithLanguage<number>>): SagaIterator {
	try {
		const response = yield* call(Api.Team.getShareImage, payload);
		ApiError.CHECK(response);
		yield* put(fetchSharingImageSuccess(response.success));
	} catch (e) {
		const error = e as ApiError;
		yield* put(sharingImageLoaded(true));
		yield* put(toggleErrorModal(error.message));
	}
};
